import {
  TOOLTIP_ARROW_CLASS,
  TOOLTIP_ARROW_SIZE,
  TOOLTIP_CONTENT_BASE_CLASS,
  TOOLTIP_SIDE_CLASS,
} from '@/componentes/ui/tooltip'

const VIEWPORT_PADDING = 8
const SIDE_OFFSET = 10

let tooltipRoot
let tooltipArrow
let activeInstance
let repositionHandler

const clamp = (value, min, max) => {
  return Math.min(Math.max(value, min), max)
}

const ensureTooltip = () => {
  if (tooltipRoot) {
    return tooltipRoot
  }

  tooltipRoot = document.createElement('div')
  tooltipRoot.className = TOOLTIP_CONTENT_BASE_CLASS
  tooltipRoot.setAttribute('role', 'tooltip')
  tooltipRoot.setAttribute('aria-hidden', 'true')
  tooltipRoot.style.position = 'fixed'
  tooltipRoot.style.left = '0'
  tooltipRoot.style.top = '0'
  tooltipRoot.style.visibility = 'hidden'
  tooltipRoot.style.pointerEvents = 'none'
  tooltipRoot.style.maxWidth = '20rem'

  tooltipArrow = document.createElement('div')
  tooltipArrow.className = TOOLTIP_ARROW_CLASS
  tooltipRoot.appendChild(tooltipArrow)

  document.body.appendChild(tooltipRoot)

  return tooltipRoot
}

const clearTooltipSideClass = () => {
  if (!tooltipRoot) {
    return
  }

  Object.values(TOOLTIP_SIDE_CLASS).forEach((className) => {
    tooltipRoot.classList.remove(...className.split(' '))
  })
}

const parseTooltipBinding = (binding) => {
  const modifiers = binding && binding.modifiers ? binding.modifiers : {}
  const value = binding ? binding.value : null

  const preferredSide = modifiers.top
    ? 'top'
    : modifiers.right
      ? 'right'
      : modifiers.bottom
        ? 'bottom'
        : modifiers.left
          ? 'left'
          : null

  if (value == null) {
    return { text: '', side: preferredSide || 'top' }
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return { text: String(value), side: preferredSide || 'top' }
  }

  if (typeof value === 'object') {
    const textSource =
      value.text ?? value.title ?? value.content ?? value.label ?? ''
    const sideSource = preferredSide || value.placement || value.side || 'top'

    return {
      text: textSource == null ? '' : String(textSource),
      side: ['top', 'right', 'bottom', 'left'].includes(sideSource)
        ? sideSource
        : 'top',
    }
  }

  return { text: '', side: preferredSide || 'top' }
}

const chooseSide = (rect, box, preferredSide) => {
  const available = {
    top: rect.top,
    right: window.innerWidth - rect.right,
    bottom: window.innerHeight - rect.bottom,
    left: rect.left,
  }

  const required = {
    top: box.height + SIDE_OFFSET + VIEWPORT_PADDING,
    right: box.width + SIDE_OFFSET + VIEWPORT_PADDING,
    bottom: box.height + SIDE_OFFSET + VIEWPORT_PADDING,
    left: box.width + SIDE_OFFSET + VIEWPORT_PADDING,
  }

  if (available[preferredSide] >= required[preferredSide]) {
    return preferredSide
  }

  const opposite = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[preferredSide]

  if (available[opposite] >= required[opposite]) {
    return opposite
  }

  return Object.keys(available).sort((a, b) => available[b] - available[a])[0]
}

const positionArrow = (side, left, top, rect) => {
  if (!tooltipArrow || !tooltipRoot) {
    return
  }

  const arrowSize = TOOLTIP_ARROW_SIZE
  const tooltipRect = tooltipRoot.getBoundingClientRect()
  const centerX = rect.left + rect.width / 2 - left
  const centerY = rect.top + rect.height / 2 - top
  const minX = 12
  const maxX = Math.max(minX, tooltipRect.width - 12)
  const minY = 12
  const maxY = Math.max(minY, tooltipRect.height - 12)

  tooltipArrow.style.top = ''
  tooltipArrow.style.right = ''
  tooltipArrow.style.bottom = ''
  tooltipArrow.style.left = ''

  if (side === 'top') {
    tooltipArrow.style.bottom = `${Math.round(-arrowSize / 2)}px`
    tooltipArrow.style.left = `${Math.round(clamp(centerX, minX, maxX))}px`
    tooltipArrow.style.transform = 'translateX(-50%) rotate(45deg)'
    return
  }

  if (side === 'bottom') {
    tooltipArrow.style.top = `${Math.round(-arrowSize / 2)}px`
    tooltipArrow.style.left = `${Math.round(clamp(centerX, minX, maxX))}px`
    tooltipArrow.style.transform = 'translateX(-50%) rotate(45deg)'
    return
  }

  if (side === 'left') {
    tooltipArrow.style.right = `${Math.round(-arrowSize / 2)}px`
    tooltipArrow.style.top = `${Math.round(clamp(centerY, minY, maxY))}px`
    tooltipArrow.style.transform = 'translateY(-50%) rotate(45deg)'
    return
  }

  tooltipArrow.style.left = `${Math.round(-arrowSize / 2)}px`
  tooltipArrow.style.top = `${Math.round(clamp(centerY, minY, maxY))}px`
  tooltipArrow.style.transform = 'translateY(-50%) rotate(45deg)'
}

const positionTooltip = (instance) => {
  if (!instance || !instance.el || !tooltipRoot) {
    return
  }

  const rect = instance.el.getBoundingClientRect()

  if (!rect.width && !rect.height) {
    hideTooltip(instance)
    return
  }

  clearTooltipSideClass()

  const content = ensureTooltip()
  content.textContent = instance.text
  content.appendChild(tooltipArrow)
  content.style.visibility = 'hidden'
  content.style.left = '0'
  content.style.top = '0'
  content.classList.add(...TOOLTIP_SIDE_CLASS.top.split(' '))

  const initialBox = content.getBoundingClientRect()
  const side = chooseSide(rect, initialBox, instance.side)

  clearTooltipSideClass()
  content.classList.add(...TOOLTIP_SIDE_CLASS[side].split(' '))

  const box = content.getBoundingClientRect()
  const maxLeft = Math.max(
    VIEWPORT_PADDING,
    window.innerWidth - box.width - VIEWPORT_PADDING
  )
  const maxTop = Math.max(
    VIEWPORT_PADDING,
    window.innerHeight - box.height - VIEWPORT_PADDING
  )

  let left = rect.left + rect.width / 2 - box.width / 2
  let top = rect.top - box.height - SIDE_OFFSET

  if (side === 'bottom') {
    top = rect.bottom + SIDE_OFFSET
  } else if (side === 'left') {
    left = rect.left - box.width - SIDE_OFFSET
    top = rect.top + rect.height / 2 - box.height / 2
  } else if (side === 'right') {
    left = rect.right + SIDE_OFFSET
    top = rect.top + rect.height / 2 - box.height / 2
  }

  left = clamp(left, VIEWPORT_PADDING, maxLeft)
  top = clamp(top, VIEWPORT_PADDING, maxTop)

  content.style.left = `${Math.round(left)}px`
  content.style.top = `${Math.round(top)}px`
  content.style.visibility = 'visible'
  positionArrow(side, left, top, rect)
}

const removeRepositionHandler = () => {
  if (!repositionHandler) {
    return
  }

  window.removeEventListener('resize', repositionHandler)
  window.removeEventListener('scroll', repositionHandler, true)
  repositionHandler = null
}

const hideTooltip = (instance) => {
  if (instance && activeInstance && activeInstance !== instance) {
    return
  }

  removeRepositionHandler()
  activeInstance = null

  if (!tooltipRoot) {
    return
  }

  tooltipRoot.style.visibility = 'hidden'
  tooltipRoot.setAttribute('aria-hidden', 'true')
}

const showTooltip = (instance) => {
  if (!instance || !instance.text || !instance.el) {
    return
  }

  ensureTooltip()

  if (activeInstance && activeInstance !== instance) {
    hideTooltip(activeInstance)
  }

  activeInstance = instance
  tooltipRoot.setAttribute('aria-hidden', 'false')

  removeRepositionHandler()
  repositionHandler = () => positionTooltip(instance)
  window.addEventListener('resize', repositionHandler)
  window.addEventListener('scroll', repositionHandler, true)

  positionTooltip(instance)
}

const applyTooltip = (el, binding) => {
  if (!el) {
    return
  }

  const next = parseTooltipBinding(binding)
  const state = el.__cardeaTooltip

  if (!state) {
    return
  }

  state.text = next.text
  state.side = next.side

  if (next.text) {
    el.setAttribute('aria-label', next.text)
    el.dataset.tooltipSide = next.side
  } else {
    el.removeAttribute('aria-label')
    delete el.dataset.tooltipSide
    if (activeInstance === state) {
      hideTooltip(state)
    }
  }

  el.removeAttribute('title')
}

const createState = (el) => {
  const state = {
    el,
    text: '',
    side: 'top',
    onPointerEnter() {
      showTooltip(state)
    },
    onPointerLeave() {
      hideTooltip(state)
    },
    onFocus() {
      showTooltip(state)
    },
    onBlur() {
      hideTooltip(state)
    },
    onKeydown(event) {
      if (event.key === 'Escape') {
        hideTooltip(state)
      }
    },
    onPointerDown() {
      hideTooltip(state)
    },
  }

  el.addEventListener('mouseenter', state.onPointerEnter)
  el.addEventListener('mouseleave', state.onPointerLeave)
  el.addEventListener('focusin', state.onFocus)
  el.addEventListener('focusout', state.onBlur)
  el.addEventListener('keydown', state.onKeydown)
  el.addEventListener('pointerdown', state.onPointerDown)

  el.__cardeaTooltip = state

  return state
}

const destroyState = (el) => {
  const state = el && el.__cardeaTooltip

  if (!state) {
    return
  }

  if (activeInstance === state) {
    hideTooltip(state)
  }

  el.removeEventListener('mouseenter', state.onPointerEnter)
  el.removeEventListener('mouseleave', state.onPointerLeave)
  el.removeEventListener('focusin', state.onFocus)
  el.removeEventListener('focusout', state.onBlur)
  el.removeEventListener('keydown', state.onKeydown)
  el.removeEventListener('pointerdown', state.onPointerDown)
  el.removeAttribute('aria-label')
  el.removeAttribute('title')
  delete el.dataset.tooltipSide
  delete el.__cardeaTooltip
}

const NativeTooltipDirective = {
  beforeMount(el, binding) {
    createState(el)
    applyTooltip(el, binding)
  },
  updated: applyTooltip,
  beforeUnmount(el) {
    destroyState(el)
  },
}

export default NativeTooltipDirective
